import { FC, SetStateAction, createContext, useCallback, useEffect, useRef, useState } from "react";
import { vCallIncomingMessageReceived, vCallWSStartConnecting } from "../../app/videocall/videoSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import DMSMedia from "./DMSMedia";
import { Navigate, useBeforeUnload, useParams } from "react-router-dom";
import { VideoEffect, WebRTCAdaptor, getUrlParameter } from "@antmedia/webrtc_adaptor";
//import { webRTCAdaptor } from "./main";
import { getWebSocketURLAttribute, isComponentMode, getRoomNameAttribute } from "../../utils/antmedia-utils";
import { useWebSocket } from "./Components/WebSocketProvider";
import _ from "lodash";
import { setOnCall } from "../../app/userState/userStateSlice";
import { useHttpRequest } from "../../hooks/useHttpRequest";
import { useInterval } from "../../pages/leads/hooks/useInterval";
import useSound from "use-sound";
import ringtone from "../../../../main/public/sound/ringtone.mp3";
export const ConferenceContext = createContext<any>(null);
import { MessageBox } from "../../components/message-box";

interface IProps {
    channel?: string;
    isTest?: boolean;
}
interface IDictionary {
    [index: string]: string;
}

const globals = {
    //this settings is to keep consistent with the sdk until backend for the app is setup
    // maxVideoTrackCount is the tracks i can see excluding my own local video.so the use is actually seeing 3 videos when their own local video is included.
    maxVideoTrackCount: 6,
    desiredMaxVideoTrackCount: 6,
    trackEvents: [] as any[],
};

const JoinModes = {
    MULTITRACK: "multitrack",
    MCU: "mcu"
}

function getMediaConstraints(videoSendResolution: any, frameRate: any) {
    let constraint = undefined;

    switch (videoSendResolution) {
        case "screenConstraints":
            constraint = {
                video: {
                    width: { max: window.screen.width }, height: { max: window.screen.height }, frameRate: { ideal: frameRate }
                },
                audio: true,
            };
            break;
        case "qvgaConstraints":
            constraint = {
                video: {
                    width: { ideal: 320 }, height: { ideal: 180 },
                    advanced: [
                        { frameRate: { min: frameRate } }, { height: { min: 180 } }, { width: { min: 320 } }, { frameRate: { max: frameRate } }, { width: { max: 320 } }, { height: { max: 180 } }, { aspectRatio: { exact: 1.77778 } }
                    ]
                }
            };
            break;
        case "vgaConstraints":
            constraint = {
                video: {
                    width: { ideal: 640 }, height: { ideal: 360 },
                    advanced: [
                        { frameRate: { min: frameRate } }, { height: { min: 360 } }, { width: { min: 640 } }, { frameRate: { max: frameRate } }, { width: { max: 640 } }, { height: { max: 360 } }, { aspectRatio: { exact: 1.77778 } }
                    ]
                }
            };
            break;
        case "hdConstraints":
            constraint = {
                video: {
                    width: { ideal: 1280 }, height: { ideal: 720 },
                    advanced: [
                        { frameRate: { min: frameRate } }, { height: { min: 720 } }, { width: { min: 1280 } }, { frameRate: { max: frameRate } }, { width: { max: 1280 } }, { height: { max: 720 } }, { aspectRatio: { exact: 1.77778 } }
                    ]
                }
            };
            break;
        case "fullHdConstraints":
            constraint = {
                video: {
                    width: { ideal: 1920 }, height: { ideal: 1080 },
                    advanced: [
                        { frameRate: { min: frameRate } }, { height: { min: 1080 } }, { width: { min: 1920 } }, { frameRate: { max: frameRate } }, { width: { max: 1920 } }, { height: { max: 1080 } }, { aspectRatio: { exact: 1.77778 } }
                    ]
                }
            };
            break;
        default:
            break;
    }

    return constraint;
}

function getPlayToken() {
    const dataPlayToken = document.getElementById("root")?.getAttribute("data-play-token");
    return (dataPlayToken) ? dataPlayToken : getUrlParameter("playToken");
}

function getPublishToken() {
    const dataPublishToken = document.getElementById("root")?.getAttribute("data-publish-token");
    return (dataPublishToken) ? dataPublishToken : getUrlParameter("publishToken");
}

let playToken = getPlayToken() as string | undefined;
let publishToken = getPublishToken();
let token = getUrlParameter("token") as string | undefined;
let mcuEnabled = getUrlParameter("mcuEnabled") as boolean | null;
let InitialStreamId = getUrlParameter("streamId") as string | null;
let playOnly = getUrlParameter("playOnly") as boolean | null;
let enterDirectly = getUrlParameter("enterDirectly");
//   if (enterDirectly == null || typeof enterDirectly === "undefined") {
//     enterDirectly = false;
//   }
let subscriberId = getUrlParameter("subscriberId") as string | undefined;
let subscriberCode = getUrlParameter("subscriberCode") as string | undefined;
let scrollThreshold = -Infinity;
let scroll_down = true;
let last_warning_time: number | null = null;

let videoQualityConstraints = {
    video: {
        width: { ideal: 640 }, height: { ideal: 360 },
        advanced: [
            { frameRate: { min: 15 } }, { height: { min: 360 } }, { width: { min: 640 } }, { frameRate: { max: 15 } }, { width: { max: 640 } }, { height: { max: 360 } }, { aspectRatio: { exact: 1.77778 } }
        ]
    },
}

let audioQualityConstraints = {
    audio: {
        noiseSuppression: true,
        echoCancellation: true
    }
}

let mediaConstraints = {
    // setting constraints here breaks source switching on firefox.
    video: videoQualityConstraints.video as any,
    audio: audioQualityConstraints.audio as any,
};

if (localStorage.getItem('selectedCamera')) {
    mediaConstraints.video.deviceId = localStorage.getItem('selectedCamera');
}

if (localStorage.getItem('selectedMicrophone')) {
    mediaConstraints.audio.deviceId = localStorage.getItem('selectedMicrophone');
}


if (playOnly) {
    mediaConstraints = {
        video: false,
        audio: false,
    };
}

let websocketURL = process.env.REACT_APP_VIDEO_WSDASH_ADDRESS;

if (!websocketURL) {

    websocketURL = getWebSocketURLAttribute() as string | undefined;

    if (!websocketURL) {
        const appName = window.location.pathname.substring(
            0,
            window.location.pathname.lastIndexOf("/") + 1
        );
        const path =
            window.location.hostname +
            ":" +
            window.location.port +
            appName +
            "websocket";
        websocketURL = "ws://" + path;

        if (window.location.protocol.startsWith("https")) {
            websocketURL = "wss://" + path;
        }
    }

}

let fullScreenId = -1;

if (mcuEnabled == null) {
    mcuEnabled = false;
}

if (playOnly == null) {
    playOnly = false;
}

if (playToken == null || typeof playToken === "undefined") {
    playToken = "";
}

if (publishToken == null || typeof publishToken === "undefined") {
    publishToken = "";
}

if (token == null || typeof token === "undefined") {
    token = "";
}

let roomOfStream: any[] = [];

let audioListenerIntervalJob: NodeJS.Timer | null | undefined = null;
let videoTrackAssignmentsIntervalJob: NodeJS.Timer | null = null;


let room = null;
let reconnecting = false;
let publishReconnected: boolean;
let playReconnected: boolean;
const MainPlugin: FC<IProps> = ({ channel, isTest }) => {

    const dispatch = useAppDispatch()
    const { weblinkid } = useParams()
    const initialmeetingstatus = ""
    //const initialmeetingstatus = "waiting-video-dms"
    const [meetingStatus, setMeetingStatus] = useState(initialmeetingstatus);
    const [customerName, setCustomerName] = useState("");
    const { vCallState, vCallIncomingMessage } = useAppSelector((state) => state.vcall);
    const [roomName, setRoomName] = useState("")
    const { user } = useAppSelector((state) => state.auth);
    const [webRTCAdaptor, setWebRTCAdaptor] = useState<WebRTCAdaptor>();

    // drawerOpen for message components.
    const [messageDrawerOpen, setMessageDrawerOpen] = useState(false);

    // drawerOpen for participant list components.
    const [participantListDrawerOpen, setParticipantListDrawerOpen] = useState(false);

    // drawerOpen for effects components.
    const [effectsDrawerOpen, setEffectsDrawerOpen] = useState(false);

    const [publishStreamId, setPublishStreamId] = useState(InitialStreamId);


    // this is my own name when i enter the room.
    const [streamName, setStreamName] = useState("");

    // this is for checking if i am sharing my screen with other participants.
    const [isScreenShared, setIsScreenShared] = useState(false);

    // this is for checking if my local camera is turned off.
    const [isMyCamTurnedOff, setIsMyCamTurnedOff] = useState(false);

    // this is for checking if my local mic is turned off.
    const [isMyMicMuted, setIsMyMicMuted] = useState(false);

    //we are going to store number of unread messages to display on screen if user has not opened message component.
    const [numberOfUnReadMessages, setNumberOfUnReadMessages] = useState(0);

    // hide or show the emoji reaction component.
    const [showEmojis, setShowEmojis] = useState(false);

    // open or close the mute participant dialog.
    const [isMuteParticipantDialogOpen, setMuteParticipantDialogOpen] = useState(false);

    // set participant id you wanted to mute.
    const [participantIdMuted, setParticipantIdMuted] = useState({ streamName: "", streamId: "" });

    // this one just triggers the re-rendering of the component.
    const [participantUpdated, setParticipantUpdated] = useState(false);
    const [onLeftParticipant, setOnLeftParticipant] = useState(false);

    const [isRecordPluginInstalled, setIsRecordPluginInstalled] = useState(false);

    const [isRecordPluginActive, setIsRecordPluginActive] = useState(false);

    const [roomJoinMode, setRoomJoinMode] = useState(JoinModes.MULTITRACK);

    const [waitingOrMeetingRoom, setWaitingOrMeetingRoom] = useState("waiting");
    const [leftTheRoom, setLeftTheRoom] = useState(false);
    const [unAuthorizedDialogOpen, setUnAuthorizedDialogOpen] = useState(false);

    const [isAdmin, setIsAdmin] = useState(false);
    const [approvedSpeakerRequestList, setApprovedSpeakerRequestList] = useState([]);
    const [presenters, setPresenters] = useState([]);
    const [presenterButtonDisabled, setPresenterButtonDisabled] = useState(false);
    const [microphoneButtonDisabled, setMicrophoneButtonDisabled] = useState(false);
    const [cameraButtonDisabled, setCameraButtonDisabled] = useState(false);
    const [showMessageBox, setShowMessageBox] = useState(false);
    const [message, setMessage] = useState("");
    const closeMessageBoxHandler = () => {
        setShowMessageBox((prev) => !prev);
        setMessage("");
    };

    const [screenSharingInProgress, setScreenSharingInProgress] = useState(false);

    const [reactions] = useState({
        'sparkling_heart': '💖',
        'thumbs_up': '👍🏼',
        'party_popper': '🎉',
        'clapping_hands': '👏🏼',
        'face_with_tears_of_joy': '😂',
        'open_mouth': '😮',
        'sad_face': '😢',
        'thinking_face': '🤔',
        'thumbs_down': '👎🏼'
    });

    const { sendMessage, latestMessage, isWebSocketConnected } = useWebSocket() as any;

    const [videoTrackAssignments, setVideoTrackAssignments] = useState<any[]>([]);

    /*
     * allParticipants: is a dictionary of (streamId, broadcastObject) for all participants in the room.
     * It determines the participants list in the participants drawer.
     * broadcastObject callback (which is return of getBroadcastObject request) for roomName has subtrackList and
     * we use it to fill this dictionary.
     */
    const [allParticipants, setAllParticipants] = useState({});

    const [audioTracks, setAudioTracks] = useState<any[]>([]);

    const [talkers, setTalkers] = useState([]);
    const [isPublished, setIsPublished] = useState(false);
    const [isPlayed, setIsPlayed] = useState(false);
    const [isJoining, setIsJoining] = useState(false);

    const [selectedCamera, setSelectedCamera] = useState(localStorage.getItem('selectedCamera'));
    const [selectedMicrophone, setSelectedMicrophone] = useState(localStorage.getItem('selectedMicrophone'));
    const [selectedBackgroundMode, setSelectedBackgroundMode] = useState("");
    const [isVideoEffectRunning, setIsVideoEffectRunning] = useState(false);
    const [virtualBackground, setVirtualBackground] = useState<any>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const screenShareWebRtcAdaptor = useRef<WebRTCAdaptor | null>(null)
    const screenShareStreamId = useRef<string | null>(null)
    //const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [fakeParticipantCounter, setFakeParticipantCounter] = useState(1);
    const leaveRoomWithError = useRef(false);

    // video send resolution for publishing
    // possible values: "auto", "highDefinition", "standartDefinition", "lowDefinition"
    const [videoSendResolution, setVideoSendResolution] = useState(localStorage.getItem("videoSendResolution") ? localStorage.getItem("videoSendResolution") : "auto");

    const [messages, setMessages] = useState([]);

    const [devices, setDevices] = useState<any[]>([]);

    const [isPlayOnly] = useState(playOnly);

    const [isEnterDirectly] = useState(enterDirectly);

    const [localVideo, setLocalVideo] = useState<any>(null);

    const [initialized, setInitialized] = useState(!!isTest);
    const [recreateAdaptor, setRecreateAdaptor] = useState(true);
    const [publisherRequestListDrawerOpen, setPublisherRequestListDrawerOpen] = useState(false);
    const [alreadyPickedUp, setAlreadyPickedUp] = useState(false)
    const { sendRequest } = useHttpRequest();




    function handleUnauthorizedDialogExitClicked() {

        setUnAuthorizedDialogOpen(false)
        setWaitingOrMeetingRoom("waiting")

    }

    function checkAndUpdateVideoAudioSources() {
        let isVideoDeviceAvailable = false;
        let isAudioDeviceAvailable = false;
        let selectedDevices = getSelectedDevices();
        let currentCameraDeviceId = selectedDevices.videoDeviceId;
        let currentAudioDeviceId = selectedDevices.audioDeviceId;

        // check if the selected devices are still available
        for (let index = 0; index < devices.length; index++) {
            if (devices[index].kind === "videoinput" && devices[index].deviceId === selectedDevices.videoDeviceId) {
                isVideoDeviceAvailable = true;
                setCameraButtonDisabled(false);
            }
            if (devices[index].kind === "audioinput" && devices[index].deviceId === selectedDevices.audioDeviceId) {
                isAudioDeviceAvailable = true;
                setMicrophoneButtonDisabled(false);
            }
        }

        // if the selected devices are not available, select the first available device
        if (selectedDevices.videoDeviceId === '' || isVideoDeviceAvailable === false) {
            const camera = devices.find(d => d.kind === 'videoinput');
            if (camera) {
                selectedDevices.videoDeviceId = camera.deviceId;
                setCameraButtonDisabled(false);
                console.info("Unable to access selected camera, switching the first available camera.");
                displayMessage("Unable to access selected camera, switching the first available camera.", "white");
            } else {
                // if there is no camera, set the video to false
                checkAndTurnOffLocalCamera()
                setCameraButtonDisabled(true)
                console.info("There is no available camera device.");
                displayMessage("There is no available camera device.", "white")
            }
        }
        if (selectedDevices.audioDeviceId === '' || isAudioDeviceAvailable === false) {
            const audio = devices.find(d => d.kind === 'audioinput');
            if (audio) {
                selectedDevices.audioDeviceId = audio.deviceId;
                setMicrophoneButtonDisabled(false);
                console.info("Unable to access selected microphone, switching the first available microphone.");
                displayMessage("Unable to access selected microphone, switching the first available microphone.", "white");
            } else {
                // if there is no audio, set the audio to false
                muteLocalMic()
                setMicrophoneButtonDisabled(true)
                console.info("There is no microphone device available.");
                displayMessage("There is no microphone device available.", "white")
            }
        }

        setSelectedDevices(selectedDevices);

        if (webRTCAdaptor !== null && currentCameraDeviceId !== selectedDevices.videoDeviceId && typeof publishStreamId != 'undefined') {
            webRTCAdaptor?.switchVideoCameraCapture(publishStreamId ?? "", selectedDevices.videoDeviceId ?? "", () => { });
        }
        if (webRTCAdaptor !== null && (currentAudioDeviceId !== selectedDevices.audioDeviceId || selectedDevices.audioDeviceId === 'default') && typeof publishStreamId != 'undefined') {
            webRTCAdaptor?.switchAudioInputSource(publishStreamId ?? "", selectedDevices.audioDeviceId ?? "");
        }
    }

    function reconnectionInProgress() {
        //reset UI releated states
        removeAllRemoteParticipants();

        reconnecting = true;
        publishReconnected = false;
        playReconnected = false;

        displayWarning("Connection lost. Trying reconnect...");
    }

    function joinRoom(roomName: string, generatedStreamId: string, roomJoinMode?: string | undefined) {
        room = roomName;
        //roomOfStream[generatedStreamId] = room;

        globals.maxVideoTrackCount = 6; //FIXME
        setPublishStreamId(generatedStreamId);

        token = getUrlParameter("token") as string | undefined || publishToken as string | undefined; // can be used for both publish and play. at the moment only used on room creation password scenario

        if (!playOnly && token === undefined) {
            token = publishToken as string | undefined;
        }

        if (!playOnly) {
            handlePublish(generatedStreamId, token, subscriberId, subscriberCode);
        }

        if (token === undefined) {
            token = playToken;
        }
        webRTCAdaptor?.play(roomName, token, roomName, undefined, subscriberId, subscriberCode);
    }



    async function checkDevices() {
        let devices = await navigator.mediaDevices.enumerateDevices();
        let audioDeviceAvailable = false
        let videoDeviceAvailable = false
        devices.forEach(device => {
            if (device.kind === "audioinput") {
                audioDeviceAvailable = true;
            }
            if (device.kind === "videoinput") {
                videoDeviceAvailable = true;
            }
        });

        if (!audioDeviceAvailable) {
            mediaConstraints.audio = false;
        }
        if (!videoDeviceAvailable) {
            mediaConstraints.video = false;
        }
    }

    function handleMainTrackBroadcastObject(broadcastObject: { metaData: string | null | undefined; subTrackStreamIds: any; }) {
        if (broadcastObject.metaData !== undefined && broadcastObject.metaData !== null) {
            let brodcastStatusMetadata = JSON.parse(broadcastObject.metaData);

            if (brodcastStatusMetadata.isRecording !== undefined && brodcastStatusMetadata.isRecording !== null) {
                setIsRecordPluginActive(brodcastStatusMetadata.isRecording);
            }
        }

        let participantIds = broadcastObject.subTrackStreamIds;

        //find and remove not available tracks
        const temp = allParticipants as any;
        let currentTracks = Object.keys(temp);
        currentTracks.forEach(trackId => {
            if (!(allParticipants as any)[trackId].isFake && !participantIds.includes(trackId)) {
                console.log("stream removed:" + trackId);

                delete temp[trackId];
            }
        });
        setAllParticipants(temp);
        setParticipantUpdated(!participantUpdated);

        //request broadcast object for new tracks
        participantIds.forEach((pid: string) => {
            if ((allParticipants as any)[pid] === undefined) {
                webRTCAdaptor?.getBroadcastObject(pid);
            }
        });
    }


    function handleSubtrackBroadcastObject(broadcastObject: { metaData: string; isScreenShared: any; streamId: string | number; }) {
        let allParticipantsTemp = allParticipants;
        let metaData = JSON.parse(broadcastObject.metaData);
        broadcastObject.isScreenShared = metaData.isScreenShared;
        (allParticipantsTemp as any)[broadcastObject.streamId] = broadcastObject; //TODO: optimize
        setAllParticipants(allParticipantsTemp);
        setParticipantUpdated(!participantUpdated);
    }

    const getCustomTURNServer = () => {
        let pc_config = null
        console.log("pc>>>", String(process.env.REACT_APP_CUSTOM_TURN_SERVER))
        if (process.env.REACT_APP_CUSTOM_TURN_SERVER) {
            pc_config =
            {
                'iceServers': [
                    {
                        'url': 'stun:stun1.l.google.com:19302'
                    },
                    {
                        'url': process.env.REACT_APP_CUSTOM_TURN_SERVER,
                        'username': process.env.REACT_APP_CUSTOM_TURN_USERNAME,
                        'credential': process.env.REACT_APP_CUSTOM_TURN_PASSWORD
                    }
                ]
            }
        } else {
            pc_config =
            {
                'iceServers': [
                    {
                        'urls': 'stun:stun1.l.google.com:19302'
                    }
                ]
            }
        }
        return pc_config;
    }
    useEffect(() => {
        console.log("idris> this is time to check webadaptor ", meetingStatus)
        async function createWebRTCAdaptor() {
            console.log("----------------- createWebRTCAdaptor", websocketURL, mediaConstraints, playOnly);
            //here we check if audio or video device available and wait result
            //according to the result we modify mediaConstraints
            await checkDevices();
            if (recreateAdaptor && webRTCAdaptor == null) {
                const pc_config = getCustomTURNServer()
                console.log("pc config", pc_config)
                setWebRTCAdaptor(new WebRTCAdaptor({
                    websocket_url: websocketURL,
                    mediaConstraints: mediaConstraints,
                    isPlayMode: playOnly,
                    // onlyDataChannel: playOnly,
                    debug: true,
                    callback: infoCallback,
                    callbackError: errorCallback,
                    peerconnection_config: pc_config,

                }))

                setRecreateAdaptor(false);
            }
        }
        if (meetingStatus != "" && !initialized)
            createWebRTCAdaptor();
    }, [recreateAdaptor, meetingStatus]);  // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        console.log("idris> meeting status=", meetingStatus)
        if (webRTCAdaptor == null && meetingStatus != "") {
            console.log("idris>recreating adaptor from meeting status")
            setRecreateAdaptor(true)
        }
    }, [meetingStatus])
    useEffect(() => {
        if (devices.length > 0) {
            checkAndUpdateVideoAudioSources();
        }
    }, [devices]); // eslint-disable-line react-hooks/exhaustive-deps

    if (webRTCAdaptor) {
        webRTCAdaptor.callback = infoCallback;
        webRTCAdaptor.callbackError = errorCallback;
        //webRTCAdaptor.localStream = localVideo;idris
    }

    function makeid(length: number) {
        let result = '';
        let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let charactersLength = characters.length;
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    useEffect(() => {
        if ((isPublished || isPlayOnly) && isPlayed) {
            setWaitingOrMeetingRoom("meeting")
            setIsJoining(false);
        }
    }, [isPublished, isPlayed, isPlayOnly])
    function createScreenShareWebRtcAdaptor() {

        navigator.mediaDevices.getDisplayMedia(getMediaConstraints("screenConstraints", 20))
            .then((stream) => {
                if (stream !== null && stream !== undefined && stream.getVideoTracks().length > 0) {
                    // it handles the stop screen sharing event
                    stream.getVideoTracks()[0].addEventListener('ended', () => {
                        handleStopScreenShare();
                    });
                }
                screenShareWebRtcAdaptor.current = new WebRTCAdaptor({
                    websocket_url: websocketURL,
                    localStream: stream,
                    mediaConstraints: getMediaConstraints("screenConstraints", 20),
                    sdp_constraints: {
                        OfferToReceiveAudio: false,
                        OfferToReceiveVideo: false,
                    },
                    debug: true,
                    callback: screenShareWebRtcAdaptorInfoCallback,
                    callbackError: screenShareWebRtcAdaptorErrorCallback
                })

            }).catch(error => {
                console.log(error)
            })

    }

    function startScreenSharing() {

        let token = getUrlParameter("token") || publishToken; // can be used for both publish and play. at the moment only used on room creation password scenario

        if (token === undefined) {
            token = publishToken;
        }

        let metaData = {
            isMicMuted: false,
            isCameraOn: true,
            isScreenShared: true,
            playOnly: false
        }

        let currentStreamName = streamName + " - Screen Share";

        screenShareStreamId.current = publishStreamId + "_presentation"

        screenShareWebRtcAdaptor.current?.publish(screenShareStreamId.current, token as string | undefined, subscriberId,
            subscriberCode, currentStreamName, roomName, JSON.stringify(metaData))

        setScreenSharingInProgress(true);

        setTimeout(() => {
            setScreenSharingInProgress(false);
        }, 5000);
    }

    useEffect(() => {

        if (playOnly && enterDirectly && initialized) {
            let streamId = makeid(10);
            setStreamName("Anonymous");

            // if play only mode and enter directly flags are true, then we will enter the meeting room directly
            setWaitingOrMeetingRoom("meeting");

            joinRoom(roomName, streamId, roomJoinMode);
        }


        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialized]);
    useInterval(() => {
        webRTCAdaptor?.requestVideoTrackAssignments(roomName);
    }, isPlayed ? 3000 : null)
    function infoCallback(info: string, obj: any) {
        if (info === "initialized") {
            enableDisableMCU(mcuEnabled);
            setInitialized(true);
        } else if (info === "broadcastObject") {
            if (obj.broadcast === undefined) {
                return;
            }

            let broadcastObject = JSON.parse(obj.broadcast);

            if (obj.streamId === roomName) { //maintrack object
                handleMainTrackBroadcastObject(broadcastObject);
            } else { //subtrack object
                handleSubtrackBroadcastObject(broadcastObject);
            }

            console.log(obj.broadcast);
        } else if (info === "newStreamAvailable") {
            handlePlayVideo(obj);
            console.log("newStreamAvailable:", obj);
        } else if (info === "publish_started") {
            setIsPublished(true);
            console.log("**** publish started:" + reconnecting);

            if (reconnecting) {
                // we need to set the local video again after the reconnection
                let newLocalVideo = document.getElementById((typeof publishStreamId ?? "undefined" === "undefined") ? "localVideo" : publishStreamId ?? "");
                localVideoCreate(newLocalVideo);
                // we need to set the setVideoCameraSource to be able to update sender source after the reconnection
                webRTCAdaptor?.mediaManager.setVideoCameraSource(publishStreamId, webRTCAdaptor.mediaManager.mediaConstraints, null, true);
                webRTCAdaptor?.getBroadcastObject(roomName); // FIXME: maybe this is not needed, check it
                publishReconnected = true;
                reconnecting = !(publishReconnected && playReconnected);
                return;
            }
            console.log("publish started");
            //stream is being published
            webRTCAdaptor?.enableStats(publishStreamId ?? "");
        } else if (info === "publish_finished") {
            setIsPublished(false);
            //stream is being finished
        } else if (info === "play_finished") {
            console.log("play_finished")
            setIsPlayed(false);
            //stream is being finished
        } else if (info === "session_restored") {
            console.log("**** session_restored:" + reconnecting);
            if (reconnecting) {
                publishReconnected = true;
                reconnecting = !(publishReconnected && playReconnected);
            }
        } else if (info === "play_started") {
            console.log("**** play started:" + reconnecting);
            setIsPlayed(true);

            webRTCAdaptor?.getBroadcastObject(roomName);


            if (reconnecting) {
                playReconnected = true;
                reconnecting = !(publishReconnected && playReconnected);
            }
        } else if (info === "screen_share_stopped") {

        } else if (info === "screen_share_started") {

        } else if (info === "data_received") {
            try {
                handleNotificationEvent(obj);
            } catch (e) {
            }
        } else if (info === "available_devices") {
            setDevices(obj.filter((x: any) => !x.label.includes("OBS")));

        } else if (info === "updated_stats") {
            checkConnectionQuality(obj);
        } else if (info === "debugInfo") {
            handleDebugInfo(obj.debugInfo);
        } else if (info === "ice_connection_state_changed") {
            console.log("iceConnectionState Changed: ", JSON.stringify(obj))
            let iceState = obj.state;
            if (iceState === "failed" || iceState === "disconnected" || iceState === "closed") {

                setTimeout(() => {
                    if (webRTCAdaptor?.iceConnectionState(publishStreamId ?? "") !== "checking" &&
                        webRTCAdaptor?.iceConnectionState(publishStreamId ?? "") !== "connected" &&
                        webRTCAdaptor?.iceConnectionState(publishStreamId ?? "") !== "completed") {
                        reconnectionInProgress();
                    }
                }, 5000);

            }
        }
    }

    function checkConnectionQuality(obj: { videoRoundTripTime: string; audioRoundTripTime: string; videoJitter: string; audioJitter: string; currentOutgoingBitrate: string; videoPacketsLost: string; audioPacketsLost: string; totalVideoPacketsSent: string; totalAudioPacketsSent: string; availableOutgoingBitrate: number; }) {
        let rtt = ((parseFloat(obj.videoRoundTripTime) + parseFloat(obj.audioRoundTripTime)) / 2).toPrecision(3) as unknown as number;
        let jitter = ((parseFloat(obj.videoJitter) + parseInt(obj.audioJitter)) / 2).toPrecision(3) as unknown as number;
        let outgoingBitrate = parseInt(obj.currentOutgoingBitrate);

        let packageLost = parseInt(obj.videoPacketsLost) + parseInt(obj.audioPacketsLost);
        let packageSent = parseInt(obj.totalVideoPacketsSent) + parseInt(obj.totalAudioPacketsSent);
        let packageLostPercentage = 0;
        if (packageLost > 0) {
            packageLostPercentage = ((packageLost / packageSent) * 100).toPrecision(3) as unknown as number;
        }

        if (rtt >= 150 || packageLostPercentage >= 2.5 || jitter >= 80 || ((outgoingBitrate / 100) * 80) >= obj.availableOutgoingBitrate) {
            console.warn("rtt:" + rtt + " packageLostPercentage:" + packageLostPercentage + " jitter:" + jitter + " Available Bandwidth kbps :", obj.availableOutgoingBitrate, "Outgoing Bandwidth kbps:", outgoingBitrate);
            displayPoorNetworkConnectionWarning();
        }
    }

    function screenShareWebRtcAdaptorInfoCallback(info: string, obj: { videoRoundTripTime: string; audioRoundTripTime: string; videoJitter: string; audioJitter: string; currentOutgoingBitrate: string; videoPacketsLost: string; audioPacketsLost: string; totalVideoPacketsSent: string; totalAudioPacketsSent: string; availableOutgoingBitrate: number; }) {
        if (info === "initialized") {
            startScreenSharing();
        } else if (info === "publish_started") {
            let notEvent = {
                streamId: screenShareStreamId.current,
                eventType: "SCREEN_SHARED_ON"
            };
            console.info("send notification event", notEvent);
            webRTCAdaptor?.sendData(publishStreamId ?? "", JSON.stringify(notEvent));

            setIsScreenShared(true);
            //webRTCAdaptor?.assignVideoTrack("videoTrack0", screenShareStreamId.current, true);
            //pinVideo(screenShareStreamId.current);

        } else if (info === "updated_stats") {
            checkConnectionQuality(obj);
        } else if (info === "ice_connection_state_changed") {
            //FIXME: handle reconnection
        }
    }


    function screenShareWebRtcAdaptorErrorCallback(error: string, message: string) {
        console.log("error from screen share webrtc adaptor callback")
        //some of the possible errors, NotFoundError, SecurityError,PermissionDeniedError
        console.log("error:" + error + " message:" + message);
    }

    function errorCallback(error: string | string[], message: string) {

        //some of the possible errors, NotFoundError, SecurityError,PermissionDeniedError
        let errorMessage = JSON.stringify(error);
        if (typeof message != "undefined") {
            errorMessage = message;
        }
        if (error.indexOf("no_active_streams_in_room") !== -1) {
            errorMessage = "No active stream in the room.";
        }
        errorMessage = JSON.stringify(error);
        if (error.indexOf("NotFoundError") !== -1) {
            errorMessage =
                "Camera or Mic are not found or not allowed in your device.";
            alert(errorMessage);
        } else if (
            error.indexOf("NotReadableError") !== -1 ||
            error.indexOf("TrackStartError") !== -1
        ) {
            errorMessage =
                "Camera or Mic is being used by some other process that does not not allow these devices to be read.";
            displayWarning(errorMessage);

        } else if (
            error.indexOf("OverconstrainedError") !== -1 ||
            error.indexOf("ConstraintNotSatisfiedError") !== -1
        ) {
            errorMessage =
                "There is no device found that fits your video and audio constraints. You may change video and audio constraints.";
            alert(errorMessage);
        } else if (
            error.indexOf("NotAllowedError") !== -1 ||
            error.indexOf("PermissionDeniedError") !== -1
        ) {
            errorMessage = "You are not allowed to access camera and mic.";
        } else if (error.indexOf("TypeError") !== -1) {
            errorMessage = "Video/Audio is required.";
            displayWarning(errorMessage);
            webRTCAdaptor?.mediaManager.getDevices();
        } else if (error.indexOf("UnsecureContext") !== -1) {
            errorMessage =
                "Fatal Error: Browser cannot access camera and mic because of unsecure context. Please install SSL and access via https";
        } else if (error.indexOf("WebSocketNotSupported") !== -1) {
            errorMessage = "Fatal Error: WebSocket not supported in this browser";
        } else if (error.indexOf("no_stream_exist") !== -1) {
            //TODO: removeRemoteVideo(error.streamId);
        } else if (error.indexOf("data_channel_error") !== -1) {
            errorMessage = "There was a error during data channel communication";
        } else if (error.indexOf("ScreenSharePermissionDenied") !== -1) {
            errorMessage = "You are not allowed to access screen share";
        } else if (error.indexOf("WebSocketNotConnected") !== -1) {
            errorMessage = "WebSocket Connection is disconnected.";
        } else if (error.indexOf("already_publishing") !== -1) {
            console.log("**** already publishing:" + reconnecting);
            if (reconnecting) {
                webRTCAdaptor?.stop(publishStreamId ?? "");

                setTimeout(() => {
                    handlePublish(
                        publishStreamId,
                        publishToken,
                        subscriberId,
                        subscriberCode
                    );
                }, 2000);
            }
        } else if (error.indexOf("unauthorized_access") !== -1) {
            handleLeaveFromRoom()

            setUnAuthorizedDialogOpen(true)
        }
        else if (error.indexOf("highResourceUsage") !== -1) {
            if (!isJoining && roomName && publishStreamId) {
                setTimeout(() => {
                    webRTCAdaptor?.closeWebSocket();
                    if (!playOnly) {
                        webRTCAdaptor?.stop(publishStreamId);
                    }
                    webRTCAdaptor?.stop(roomName);
                    webRTCAdaptor?.checkWebSocketConnection();
                    joinRoom(roomName, publishStreamId);
                }, 3000);
            }
        }
        else if ((error === "publishTimeoutError") && (!reconnecting)) {
            console.error(error, "Firewall might be blocking the connection Please setup a TURN Server");
            leaveRoomWithError.current = true;
            setLeftTheRoom(true);
        }
        else if (error === "license_suspended_please_renew_license") {
            console.error(error, "Licence is Expired please renew the licence");
            leaveRoomWithError.current = true;
            setLeftTheRoom(true);
        } else if (error === "notSetRemoteDescription") {
            console.error(error, "Not set remote description");
            leaveRoomWithError.current = true;
            setLeftTheRoom(true);
        }
        console.log("***** " + error)
    }

    function pinVideo(streamId: string | null) {

        // id is for pinning user.
        let videoLabel;
        let broadcastObject = (allParticipants as any)[streamId ?? ""];

        if (broadcastObject === undefined) {
            console.error("Cannot find broadcast object for streamId: " + streamId);
            return;
        }

        //if we already pin the targeted user then we are going to remove it from pinned video.
        if ((typeof broadcastObject.isPinned !== "undefined") && (broadcastObject.isPinned === true)) {
            broadcastObject.isPinned = false; // false means user unpin manually
            (allParticipants as any)[streamId ?? ""] = broadcastObject;
            console.log("handle unpinning video...", streamId !== publishStreamId ? streamId : publishStreamId)
            handleNotifyUnpinUser(streamId !== publishStreamId ? streamId : publishStreamId);
            setParticipantUpdated(!participantUpdated);
            return;
        }

        // if there is no pinned video we are going to pin the targeted user.
        // and we need to inform pinned user.
        if (streamId === publishStreamId) {
            videoLabel = "localVideo";
        }

        if (videoLabel !== "localVideo" && videoTrackAssignments.length > 0) {
            videoLabel = videoTrackAssignments[1]?.videoLabel;
            webRTCAdaptor?.assignVideoTrack(videoLabel, streamId ?? "", true);
        }

        Object.keys(allParticipants).forEach(id => {
            let participant = (allParticipants as any)[id];
            if (typeof participant.isPinned !== 'undefined'
                && participant.isPinned === true) {

                participant.isPinned = false;
                (allParticipants as any)[id] = participant;
            }
        });

        broadcastObject.isPinned = true;
        (allParticipants as any)[streamId ?? ""] = broadcastObject;

        handleNotifyPinUser(streamId !== publishStreamId ? streamId : publishStreamId);

        setParticipantUpdated(!participantUpdated);
    }

    function handleNotifyPinUser(id: string | null) {

        if (id === "localVideo") {
            // if we pin local video then we are not going to inform anyone.
            return;
        }
        // If I PIN USER then i am going to inform pinned user.
        // Why? Because if i pin someone, pinned user's resolution has to change for better visibility.

        handleSendNotificationEvent("PIN_USER", publishStreamId, {
            streamId: id,
        });

    }

    function handleNotifyUnpinUser(id: string | null) {
        // If I UNPIN USER then i am going to inform pinned user.
        // Why? We need to decrease resolution for pinned user's internet usage.
        handleSendNotificationEvent("UNPIN_USER", publishStreamId, {
            streamId: id,
        });
    }

    function handleSetMaxVideoTrackCount(maxTrackCount: number) {
        globals.desiredMaxVideoTrackCount = maxTrackCount;
    }

    function updateMaxVideoTrackCount(newCount: number) {
        if (publishStreamId && globals.maxVideoTrackCount !== newCount) {
            globals.maxVideoTrackCount = newCount;
            webRTCAdaptor?.setMaxVideoTrackCount(publishStreamId, newCount);
        }
    }

    function enableDisableMCU(isMCUEnabled: boolean | null) {
        if (isMCUEnabled) {
            setRoomJoinMode(JoinModes.MCU);
        } else {
            setRoomJoinMode(JoinModes.MULTITRACK);
        }
    }

    function handleStartScreenShare() {

        createScreenShareWebRtcAdaptor()

    }

    function turnOffYourMicNotification(participantId: any) {
        handleSendNotificationEvent(
            "TURN_YOUR_MIC_OFF",
            publishStreamId,
            {
                streamId: participantId,
                senderStreamId: publishStreamId
            }
        );
    }

    function startRecord() {

        displayMessage("Recording is about to start...", "white")
        let jsCmd = {
            command: "startRecording",
            streamId: roomName,
            websocketURL: websocketURL,
            token: token
        };

        sendMessage(JSON.stringify(jsCmd));
    }

    function stopRecord() {
        displayMessage("Recording is about to stop...", "white")
        let jsCmd = {
            command: "stopRecording",
            streamId: roomName,
        };

        sendMessage(JSON.stringify(jsCmd));
    }

    function sendReactions(reaction: any) {
        handleSendNotificationEvent(
            "REACTIONS",
            publishStreamId,
            {
                reaction: reaction,
                senderStreamId: publishStreamId,
            }
        );
        // showReactions(publishStreamId, reaction);
    }

    const displayPoorNetworkConnectionWarning = () => {
        console.log("displayPoorNetworkConnectionWarning");

        if (last_warning_time == null || Date.now() - last_warning_time > 1000 * 30) {
            last_warning_time = Date.now();
            displayWarning("Your connection is not stable. Please check your internet connection!");
        }
    }

    // const displayMessage = useCallback((message, color) => {
    //     closeSnackbar();
    //     enqueueSnackbar(message,
    //         {
    //             icon: <SvgIcon size={24} name={'report'} color={color} />,
    //             variant: "info",
    //             autoHideDuration: 5000,
    //             anchorOrigin: {
    //                 vertical: "top",
    //                 horizontal: "right",
    //             },
    //         }
    //     );
    // }, [closeSnackbar, enqueueSnackbar]);
    const displayMessage = (message: string, color: string = "black") => {
        console.log("displaymessage:", message, color)
    }

    const displayWarning = (message: string) => {
        displayMessage(message, "red");
    }

    function handleStopScreenShare() {
        setIsScreenShared(false);
        screenShareWebRtcAdaptor?.current?.stop(screenShareStreamId?.current ?? "");
    }



    useEffect(() => {
        scrollToBottom();
    }, [messages]);


    function scrollToBottom() {
        let objDiv = document.getElementById("paper-props");
        if (objDiv && scroll_down && objDiv.scrollHeight > objDiv.clientHeight) {
            objDiv.scrollTo(0, objDiv.scrollHeight);
            scrollThreshold = 0.95;
            scroll_down = false;
        }

    }



    function handleParticipantListOpen(open: boolean | ((prevState: boolean) => boolean)) {
        setParticipantListDrawerOpen(open);
        if (open) {
            setMessageDrawerOpen(false);
            setEffectsDrawerOpen(false);
        }
    }

    function handleEffectsOpen(open: boolean | ((prevState: boolean) => boolean)) {
        setEffectsDrawerOpen(open);
        if (open) {
            setMessageDrawerOpen(false);
            setParticipantListDrawerOpen(false);
        }
    }



    function handleDebugInfo(debugInfo: string) {
        let infoText = "Client Debug Info\n";
        infoText += "Events:\n";
        infoText += JSON.stringify(globals.trackEvents) + "\n";
        infoText += "Video Track Assignments (" + videoTrackAssignments.length + "):\n\n";
        infoText += JSON.stringify(videoTrackAssignments) + "\n\n";
        infoText += "All Participants (" + Object.keys(allParticipants).length + "):\n";
        Object.entries(allParticipants).forEach(([key, value]) => {
            infoText += "- " + key + "\n";
        });
        //infoText += JSON.stringify(allParticipants) + "\n";
        infoText += "----------------------\n";
        infoText += debugInfo;

        //fake message to add chat
        let obj = {
            streamId: publishStreamId,
            data: JSON.stringify({
                eventType: "MESSAGE_RECEIVED",
                name: "Debugger",
                date: new Date().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                }),
                message: infoText,
            }),
        };

        handleNotificationEvent(obj);
    }

    function calculate_scroll_height() {
        let objDiv = document.getElementById("paper-props");
        if (objDiv) {
            let scrollPosition = objDiv.scrollTop / (objDiv.scrollHeight - objDiv.clientHeight);
            if (scrollPosition > scrollThreshold) {
                scroll_down = true;
            }
        }
    }

    function handleNotificationEvent(obj: { streamId?: string | null; data: any; }) {
        let notificationEvent = JSON.parse(obj.data);
        if (notificationEvent != null && typeof notificationEvent == "object") {
            let eventStreamId = notificationEvent.streamId;
            let eventType = notificationEvent.eventType;

            if (eventType === "CAM_TURNED_OFF" ||
                eventType === "CAM_TURNED_ON" ||
                eventType === "MIC_MUTED" ||
                eventType === "MIC_UNMUTED") {
                webRTCAdaptor?.getBroadcastObject(eventStreamId);
            } else if (eventType === "RECORDING_TURNED_ON") {
                setIsRecordPluginActive(true);
            } else if (eventType === "RECORDING_TURNED_OFF") {
                setIsRecordPluginActive(false);
            } else if (eventType === "MESSAGE_RECEIVED") {
                // if message arrives from myself or footer message button is disabled then we are not going to show it.
                if (notificationEvent.senderId === publishStreamId
                    || process.env.REACT_APP_FOOTER_MESSAGE_BUTTON_VISIBILITY === 'false') {
                    return;
                }
                calculate_scroll_height();

                //            } else if (eventType === "REACTIONS" && notificationEvent.senderStreamId !== publishStreamId) {
                //showReactions(notificationEvent.senderStreamId, notificationEvent.reaction);
            } else if (eventType === "TURN_YOUR_MIC_OFF") {
                if (publishStreamId === notificationEvent.streamId) {
                    console.warn(notificationEvent.senderStreamId, "muted you");
                    muteLocalMic();
                }
            } else if (eventType === "PIN_USER") {
                if (
                    notificationEvent.streamId === publishStreamId &&
                    !isScreenShared
                ) {
                    updateVideoSendResolution(true);
                }
            } else if (eventType === "UNPIN_USER") {
                if (
                    notificationEvent.streamId === publishStreamId &&
                    !isScreenShared
                ) {
                    updateVideoSendResolution(false);
                }
            } else if (eventType === "VIDEO_TRACK_ASSIGNMENT_LIST") {
                let videoTrackAssignmentList = notificationEvent.payload;
                let tempVideoTrackAssignments = videoTrackAssignments;
                let tempVideoTrackAssignmentsNew: any[] = [];

                tempVideoTrackAssignments.forEach(tempVideoTrackAssignment => {
                    let assignment;

                    videoTrackAssignmentList.forEach((videoTrackAssignment: { videoLabel: any; }) => {
                        if (tempVideoTrackAssignment.videoLabel === videoTrackAssignment.videoLabel) {
                            assignment = videoTrackAssignment;
                        }
                    });
                    if (tempVideoTrackAssignment.isMine || assignment !== undefined) {
                        tempVideoTrackAssignmentsNew.push(tempVideoTrackAssignment);
                    } else {
                        console.log("---> Removed video track assignment: " + tempVideoTrackAssignment.videoLabel);
                    }
                });

                tempVideoTrackAssignments = tempVideoTrackAssignmentsNew;

                //add and/or update participants according to current assignments
                videoTrackAssignmentList.forEach((vta: { videoLabel: any; trackId: any; }) => {
                    tempVideoTrackAssignments.forEach((oldVTA) => {
                        if (oldVTA.videoLabel === vta.videoLabel) {
                            oldVTA.streamId = vta.trackId;
                        }
                    });
                });

                setVideoTrackAssignments(tempVideoTrackAssignments);

                checkScreenSharingStatus();

                setParticipantUpdated(!participantUpdated);
            } else if (eventType === "AUDIO_TRACK_ASSIGNMENT") {

                clearInterval(timeoutRef.current as NodeJS.Timeout);
                timeoutRef.current = setTimeout(() => {
                    setTalkers([]);
                }, 1000);
                //console.log(JSON.stringify(notificationEvent.payload));
                setTalkers((oldTalkers) => {
                    const newTalkers = notificationEvent.payload
                        .filter(
                            (p: { trackId: string; audioLevel: number; }) =>
                                p.trackId !== "" &&
                                p.audioLevel < 60
                        )
                        .map((p: { trackId: any; }) => p.trackId);
                    return _.isEqual(oldTalkers, newTalkers) ? oldTalkers : newTalkers;
                });
            } else if (eventType === "TRACK_LIST_UPDATED") {
                console.debug("TRACK_LIST_UPDATED -> ", obj);

                webRTCAdaptor?.getBroadcastObject(roomName);
            }
        }
    }

    function checkScreenSharingStatus() {

        const broadcastObjectsArray = Object.values(allParticipants) as any[];
        broadcastObjectsArray.forEach((broadcastObject) => {
            if (broadcastObject.isScreenShared === true && typeof broadcastObject.isPinned === "undefined") {
                pinVideo(broadcastObject.streamId);
            }
        }
        )
    }

    function getUserStatusMetadata(isMicMuted: boolean | null, isCameraOn: boolean, isScreenShareActive: boolean) {
        let metadata = {
            isMicMuted: isMicMuted === null ? null : isMicMuted,
            isCameraOn: isCameraOn,
            isScreenShared: isScreenShareActive,
            playOnly: playOnly
        }

        return metadata;
    }

    const updateRoomRecordingStatus = useCallback((isRecording) => {
        let metadata: any = {};
        if (isRecording) {
            metadata.isRecording = true;
        } else {
            metadata.isRecording = false;
        }
        webRTCAdaptor?.updateStreamMetaData(roomName, JSON.stringify(metadata));
    }, [webRTCAdaptor, roomName]);

    function updateUserStatusMetadata(micMuted: boolean | null, cameraOn: boolean) {
        let metadata = getUserStatusMetadata(micMuted, cameraOn, false);

        webRTCAdaptor?.updateStreamMetaData(publishStreamId ?? "", JSON.stringify(metadata));
    }

    function handleLeaveFromRoom() {


        // we need to empty participant array. if we are going to leave it in the first place.
        setVideoTrackAssignments([]);
        setAllParticipants({});

        clearInterval(audioListenerIntervalJob as NodeJS.Timer);
        audioListenerIntervalJob = null;

        if (!playOnly) {
            webRTCAdaptor?.stop(publishStreamId ?? "");
        }
        webRTCAdaptor?.stop(roomName);

        if (!playOnly) {
            webRTCAdaptor?.turnOffLocalCamera(publishStreamId ?? "");
        }
        setWaitingOrMeetingRoom("waiting");
    }

    // when user closes the tab or refreshes the page
    // we need to leave the room
    useBeforeUnload((ev) => {
        handleLeaveFromRoom();
    });

    const handleSendNotificationEvent = useCallback((eventType, publishStreamId, info?) => {
        let notEvent = {
            streamId: publishStreamId,
            eventType: eventType,
            ...(info ? info : {}),
        };
        console.info("send notification event", notEvent);
        webRTCAdaptor?.sendData(publishStreamId, JSON.stringify(notEvent));
    }, [webRTCAdaptor]);

    function updateVideoSendResolution(isPinned: boolean) {
        let promise = null;
        let mediaConstraints: any = { video: true };

        if (isScreenShared) {
            mediaConstraints = getMediaConstraints("screenConstraints", 20);
            promise = webRTCAdaptor?.applyConstraints(mediaConstraints);
        } else if (videoSendResolution === "auto" && !isPinned) {
            mediaConstraints = getMediaConstraints("qvgaConstraints", 15);
            promise = webRTCAdaptor?.applyConstraints(mediaConstraints);
        } else if (videoSendResolution === "auto" && isPinned) {
            mediaConstraints = getMediaConstraints("qvgaConstraints", 25);
            promise = webRTCAdaptor?.applyConstraints(mediaConstraints);
        } else if (videoSendResolution === "highDefinition") {
            mediaConstraints = getMediaConstraints("hdConstraints", 15);
            promise = webRTCAdaptor?.applyConstraints(mediaConstraints);
        } else if (videoSendResolution === "standardDefinition") {
            mediaConstraints = getMediaConstraints("vgaConstraints", 15);
            promise = webRTCAdaptor?.applyConstraints(mediaConstraints);
        } else if (videoSendResolution === "lowDefinition") {
            mediaConstraints = getMediaConstraints("qvgaConstraints", 15);
            promise = webRTCAdaptor?.applyConstraints(mediaConstraints);
        } else {
            console.error("Unknown camera resolution: " + videoSendResolution);
        }

        if (promise !== null) {
            promise?.then(() => {
                console.info("Camera resolution is updated to " + videoSendResolution + " mode");
                let videoTrackSettings = webRTCAdaptor?.mediaManager?.localStream?.getVideoTracks()[0]?.getSettings();
                console.info("Video track resolution: ", videoTrackSettings?.width, "x", videoTrackSettings?.height, " frame rate: ", videoTrackSettings?.frameRate);
            }).catch((err: string) => {
                setVideoSendResolution("auto");
                console.error("Camera resolution is not updated to " + videoSendResolution + " mode. Error is " + err);
                console.info("Trying to update camera resolution to auto");
            });
        }
    }

    function removeAllRemoteParticipants() {
        let newVideoTrackAssignment = {
            videoLabel: "localVideo",
            track: null,
            streamId: publishStreamId,
            isMine: true
        };

        let tempVideoTrackAssignments = [];
        if (!playOnly) {
            tempVideoTrackAssignments.push(newVideoTrackAssignment);
        }
        setVideoTrackAssignments(tempVideoTrackAssignments);

        let allParticipantsTemp = {};
        if (!playOnly) {
            (allParticipantsTemp as any)[publishStreamId ?? ""] = { name: "You" };
        }
        setAllParticipants(allParticipantsTemp);
    }

    function addMeAsParticipant(publishStreamId: string | number) {
        let isParticipantExist = videoTrackAssignments.find((vta) => vta.videoLabel === "localVideo");

        if (isParticipantExist || playOnly) {
            return;
        }

        let newVideoTrackAssignment = {
            videoLabel: "localVideo",
            track: null,
            streamId: publishStreamId,
            isMine: true
        };
        let tempVideoTrackAssignments = videoTrackAssignments;
        tempVideoTrackAssignments.push(newVideoTrackAssignment);
        setVideoTrackAssignments(tempVideoTrackAssignments);
        let allParticipantsTemp = allParticipants;
        (allParticipantsTemp as any)[publishStreamId] = { streamId: publishStreamId, name: "You", isPinned: false, isScreenShared: false };
        setAllParticipants(allParticipantsTemp);
    }


    function handlePublish(publishStreamId: string | null, token: string | boolean | undefined, subscriberId: string | undefined, subscriberCode: string | undefined) {
        let userStatusMetadata = getUserStatusMetadata(isMyMicMuted, !isMyCamTurnedOff, isScreenShared);

        addMeAsParticipant(publishStreamId ?? "");

        let currentStreamName = streamName;
        if (streamName === "" || streamName === undefined || streamName === null) {
            currentStreamName = "Anonymous"
        }
        webRTCAdaptor?.publish(
            publishStreamId ?? "",
            token as string | undefined,
            subscriberId,
            subscriberCode,
            currentStreamName,
            roomName,
            JSON.stringify(userStatusMetadata)
        );
    }

    function handlePlayVideo(obj: { trackId: string; track: { id: any; kind: string; }; streamId: any; }) {

        let index = obj?.trackId?.substring("ARDAMSx".length);
        globals.trackEvents.push({ track: obj.track.id, event: "added" });

        if (obj.track.kind === "audio") {
            let newAudioTrack = {
                id: index,
                track: obj.track,
                streamId: obj.streamId
            };

            //append new audio track, track id should be unique because of audio track limitation
            let temp = audioTracks;
            temp.push(newAudioTrack);
            setAudioTracks(temp);
        } else if (obj.track.kind === "video") {
            let newVideoTrackAssignment = {
                videoLabel: index,
                track: obj.track,
                streamId: obj.streamId
            };
            let tempVideoTrackAssignments = videoTrackAssignments;
            tempVideoTrackAssignments.push(newVideoTrackAssignment);
            setVideoTrackAssignments(tempVideoTrackAssignments);

        }
    }

    function setAndEnableVirtualBackgroundImage(imageUrl: string | null | undefined) {
        let virtualBackgroundImage = document.createElement("img");
        virtualBackgroundImage.id = "virtualBackgroundImage";
        virtualBackgroundImage.style.visibility = "hidden";
        virtualBackgroundImage.alt = "virtual-background";

        console.log("Virtual background image url: " + imageUrl);
        if (imageUrl !== undefined && imageUrl !== null && imageUrl !== "") {
            virtualBackgroundImage.src = imageUrl;
        } else {
            virtualBackgroundImage.src = "virtual-background0.png";
        }

        virtualBackgroundImage.onload = () => {
            if (!webRTCAdaptor) return
            console.log("Virtual background image is loaded");
            setVirtualBackground(virtualBackgroundImage);
            (webRTCAdaptor as any).setBackgroundImage(virtualBackgroundImage);

            (webRTCAdaptor as any).enableEffect(VideoEffect.VIRTUAL_BACKGROUND).then(() => {
                console.log("Effect: " + VideoEffect.VIRTUAL_BACKGROUND + " is enabled");
                setIsVideoEffectRunning(true);
            }).catch((err: string) => {
                console.error("Effect: " + VideoEffect.VIRTUAL_BACKGROUND + " is not enabled. Error is " + err);
                setIsVideoEffectRunning(false);
            });
        };
    }

    function handleBackgroundReplacement(option: string) {
        let effectName: string = "";
        if (!webRTCAdaptor)
            return
        if (option === "none") {
            effectName = VideoEffect.NO_EFFECT;
            setIsVideoEffectRunning(false);
        } else if (option === "slight-blur") {
            (webRTCAdaptor as any).setBlurEffectRange(3, 4);
            effectName = VideoEffect.BLUR_BACKGROUND;
            setIsVideoEffectRunning(true);
        } else if (option === "blur") {
            (webRTCAdaptor as any).setBlurEffectRange(6, 8);
            effectName = VideoEffect.BLUR_BACKGROUND;
            setIsVideoEffectRunning(true);
        } else if (option === "background") {
            if (virtualBackground === null) {
                setAndEnableVirtualBackgroundImage(null);
                return;
            }
            effectName = VideoEffect.VIRTUAL_BACKGROUND;
            setIsVideoEffectRunning(true);
        }

        (webRTCAdaptor as any).enableEffect(effectName).then(() => {
            console.log("Effect: " + effectName + " is enabled");
        }).catch((err: string) => {
            console.error("Effect: " + effectName + " is not enabled. Error is " + err);
            setIsVideoEffectRunning(false);
        });
    }

    function checkAndTurnOnLocalCamera(streamId: string) {
        if (isVideoEffectRunning) {
            if (webRTCAdaptor)
                webRTCAdaptor.mediaManager.localStream.getVideoTracks()[0].enabled = true;
        } else {
            webRTCAdaptor?.turnOnLocalCamera(streamId);
        }

        updateUserStatusMetadata(isMyMicMuted, true);
        setIsMyCamTurnedOff(false);

        handleSendNotificationEvent(
            "CAM_TURNED_ON",
            publishStreamId
        );
    }

    function checkAndTurnOffLocalCamera(streamId?: string | undefined) {
        if (isVideoEffectRunning) {
            if (webRTCAdaptor)
                webRTCAdaptor.mediaManager.localStream.getVideoTracks()[0].enabled = false;
        } else {
            webRTCAdaptor?.turnOffLocalCamera(streamId ?? "");
        }

        updateUserStatusMetadata(isMyMicMuted, false);
        setIsMyCamTurnedOff(true);

        handleSendNotificationEvent(
            "CAM_TURNED_OFF",
            publishStreamId
        );
    }

    function getSelectedDevices() {
        let devices = {
            videoDeviceId: selectedCamera,
            audioDeviceId: selectedMicrophone
        }
        return devices;
    }

    function setSelectedDevices(devices: { videoDeviceId?: any; audioDeviceId?: any; }) {
        if (devices.videoDeviceId !== null && devices.videoDeviceId !== undefined) {
            setSelectedCamera(devices.videoDeviceId);
            localStorage.setItem("selectedCamera", devices.videoDeviceId);
        }
        if (devices.audioDeviceId !== null && devices.audioDeviceId !== undefined) {
            setSelectedMicrophone(devices.audioDeviceId);
            localStorage.setItem("selectedMicrophone", devices.audioDeviceId);
        }
    }

    function cameraSelected(value: string | null) {
        if (selectedCamera !== value) {
            setSelectedDevices({ videoDeviceId: value });
            // When we first open home page, React will call this function and local stream is null at that time.
            // So, we need to catch the error.
            try {
                webRTCAdaptor?.switchVideoCameraCapture(publishStreamId ?? "", value ?? "", () => { });
            } catch (e) {
                console.log("Local stream is not ready yet.");
            }
        }
    }

    function microphoneSelected(value: any) {
        if (selectedMicrophone !== value) {
            setSelectedDevices({ audioDeviceId: value });
            // When we first open home page, React will call this function and local stream is null at that time.
            // So, we need to catch the error.
            try {
                webRTCAdaptor?.switchAudioInputSource(publishStreamId ?? "", value);
            } catch (e) {
                console.log("Local stream is not ready yet.");
            }
        }
    }



    function muteLocalMic() {
        webRTCAdaptor?.muteLocalMic();
        updateUserStatusMetadata(true, !isMyCamTurnedOff);
        setIsMyMicMuted(true);

        handleSendNotificationEvent(
            "MIC_MUTED",
            publishStreamId
        );
    }

    function unmuteLocalMic() {
        webRTCAdaptor?.unmuteLocalMic();
        updateUserStatusMetadata(false, !isMyCamTurnedOff);
        setIsMyMicMuted(false);

        handleSendNotificationEvent(
            "MIC_UNMUTED",
            publishStreamId
        );
    }

    const setAudioLevelListener = (listener: (arg0: any) => void, period: number | undefined) => {
        if (audioListenerIntervalJob == null) {
            audioListenerIntervalJob = setInterval(() => {
                if ((webRTCAdaptor?.remotePeerConnection as any)[publishStreamId ?? ""] !== undefined && (webRTCAdaptor?.remotePeerConnection as any)[publishStreamId ?? ""] !== null) {
                    (webRTCAdaptor?.remotePeerConnection as any)[publishStreamId ?? ""].getStats(null).then((stats: any[]) => {
                        for (const stat of stats.values()) {
                            if (stat.type === 'media-source' && stat.kind === 'audio') {
                                listener(stat?.audioLevel?.toFixed(2));
                            }
                        }
                    });
                }
            }, period);
        }
    }

    function localVideoCreate(tempLocalVideo: HTMLElement | SetStateAction<null>) {
        // it can be null when we first open the page
        // due to the fact that local stream is not ready yet.
        if (typeof tempLocalVideo !== "undefined" && tempLocalVideo !== null && webRTCAdaptor) {
            setLocalVideo(tempLocalVideo);
            webRTCAdaptor.mediaManager.localVideo = tempLocalVideo;
            webRTCAdaptor.mediaManager.localVideo.srcObject = webRTCAdaptor?.mediaManager.localStream;
        }
    }




    useEffect(() => {
        console.log("vcall restart...")
        //dispatch(vCallWSStartConnecting());

    }, [])

    useEffect(() => {
        console.log("idrisx>Incoming message> ", vCallIncomingMessage, meetingStatus)
        if (vCallIncomingMessage.requesttype === "ReceiveCall") {
            setCustomerName(vCallIncomingMessage.username)

        }
        if (user) {
            if (vCallIncomingMessage && vCallIncomingMessage.roomId !== undefined) {
                if (!alreadyPickedUp && (meetingStatus === "" || meetingStatus.includes("waiting") || meetingStatus.includes("missing") || meetingStatus === "close")) {
                    console.log("idrisx>setting up waiting again")
                    setMeetingStatus(`waiting-video-dms`)
                }
            }
        }
    }, [vCallIncomingMessage])
    const [play, { stop }] = useSound(ringtone);
    useEffect(() => {
        console.log("idrisx> meeting status", meetingStatus)
        if (meetingStatus == "waiting-video-dms") {
            play()
        } else {
            stop()
        }
    }, [meetingStatus])
    useEffect(() => {
        if (onLeftParticipant) {
            setShowMessageBox(true);
            setMessage("Oops Other participant left before you join room");
            setTimeout(() => window.location.reload(), 2000);
        }
    }, [onLeftParticipant])
    return (
        <ConferenceContext.Provider value={{
            isScreenShared,
            talkers,
            roomJoinMode,
            audioTracks,
            isPublished,
            selectedCamera,
            selectedMicrophone,
            selectedBackgroundMode,
            videoTrackAssignments,
            setVideoTrackAssignments,
            messageDrawerOpen,
            participantListDrawerOpen,
            messages,
            numberOfUnReadMessages,
            participantUpdated,
            allParticipants,
            globals,
            isPlayOnly,
            localVideo,
            streamName,
            initialized,
            devices,
            publishStreamId,
            setPublishStreamId,
            isMyMicMuted,
            isMyCamTurnedOff,
            sendReactions,
            setSelectedBackgroundMode,
            setIsVideoEffectRunning,
            isVideoEffectRunning,
            handleParticipantListOpen,
            setSelectedCamera,
            setSelectedMicrophone,
            setLeftTheRoom,
            joinRoom,
            handleStopScreenShare,
            handleStartScreenShare,
            cameraSelected,
            microphoneSelected,
            handleBackgroundReplacement,
            muteLocalMic,
            unmuteLocalMic,
            checkAndTurnOnLocalCamera,
            checkAndTurnOffLocalCamera,
            setAudioLevelListener,
            pinVideo,
            setLocalVideo,
            setWaitingOrMeetingRoom,
            setStreamName,
            roomName,
            setRoomName,
            handleLeaveFromRoom,
            handleSendNotificationEvent,
            handleSetMaxVideoTrackCount,
            turnOffYourMicNotification,
            showEmojis,
            setShowEmojis,
            isMuteParticipantDialogOpen,
            setMuteParticipantDialogOpen,
            participantIdMuted,
            setParticipantIdMuted,
            videoSendResolution,
            setVideoSendResolution,
            makeid,
            startRecord,
            stopRecord,
            isRecordPluginInstalled,
            isRecordPluginActive,
            isEnterDirectly,
            publisherRequestListDrawerOpen,
            setPublisherRequestListDrawerOpen,
            isAdmin,
            setIsAdmin,
            approvedSpeakerRequestList,
            setApprovedSpeakerRequestList,
            presenters,
            setPresenters,
            presenterButtonDisabled,
            setPresenterButtonDisabled,
            effectsDrawerOpen,
            handleEffectsOpen,
            setAndEnableVirtualBackgroundImage,
            localVideoCreate,
            microphoneButtonDisabled,
            setMicrophoneButtonDisabled,
            cameraButtonDisabled,
            setCameraButtonDisabled,
            updateMaxVideoTrackCount,
            checkAndUpdateVideoAudioSources,
            setDevices,
            getSelectedDevices,
            setIsJoining,
            isJoining,
            setParticipantUpdated,
            setOnLeftParticipant
        }}>
            <>
                <MessageBox type="warning" visible={showMessageBox} closeHandler={closeMessageBoxHandler} title="Warning" message={message} />

                {(meetingStatus.includes("waiting") || meetingStatus.includes("meeting")) &&
                    <DMSMedia meetingStatus={meetingStatus} customerName={customerName} roomName={roomName} setMeetingStatus={(status) => {
                        setMeetingStatus(status)
                    }}></DMSMedia>
                }
            </>
        </ConferenceContext.Provider>
    );
}

export default MainPlugin;
